import { config } from '../config';
import {
  AppIdentifiers,
  getToken,
  setToken,
} from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { iTrack } from '../types/ITrack';
import { base64encode, sha256 } from '../utils/cryptographicFunctions';
import { generateRandomString } from '../utils/generateRandomString';
import isElectron from '../utils/isElectron';

export default class TidalService {
  private static REDIRECT_URI_ELECTRON = 'cratehackersx://tidal/callback';
  private static REDIRECT_URI_WEB = config.BASE_URL + '/tidal/callback';

  private static CLIENT_ID = config.TIDAL_CLIENT_ID || '';
  private static CLIENT_SECRET = config.TIDAL_CLIENT_SECRET || '';

  // Scopes must match what's enabled in Tidal Developer Dashboard
  private static SCOPES = [
    'user.read',
    'collection.read',
    'collection.write',
    'playlists.read',
    'playlists.write',
  ];

  // Authorization uses login.tidal.com, token exchange uses auth.tidal.com
  private static loginUrl = 'https://login.tidal.com';
  private static tokenUrl = 'https://auth.tidal.com/v1/oauth2';
  private static apiUrl = 'https://openapi.tidal.com/v2';

  public static getAccessToken = () => {
    return getToken(AppIdentifiers.Tidal)?.accessToken;
  };

  public static getRefreshToken() {
    return getToken(AppIdentifiers.Tidal)?.refreshToken;
  }

  public static getTokenExpiration() {
    return getToken(AppIdentifiers.Tidal)?.expiration;
  }

  public static getAuthCode() {
    // With hash router, query params are inside the hash: /#/path?code=...
    // So we need to parse from hash, not from window.location.search
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex);
      const args = new URLSearchParams(queryString);
      return args.get('code');
    }
    // Fallback to regular search params
    const args = new URLSearchParams(window.location.search);
    return args.get('code');
  }

  public static async getTidalAuthorizeUrl(internalRedirectUri: string) {
    const { authUrl } = await this.getTidalAuthData(internalRedirectUri);
    return authUrl;
  }

  /**
   * Get TIDAL auth data including the auth URL and redirect URI.
   * Used by Electron popup auth to know when to intercept the callback.
   */
  public static async getTidalAuthData(internalRedirectUri: string): Promise<{
    authUrl: string;
    redirectUri: string;
    codeVerifier: string;
  }> {
    const codeVerifier = generateRandomString(128);
    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64encode(hashed);

    // Determine if running in Electron (desktop app)
    const isDesktopApp = isElectron();
    const actualTarget = isDesktopApp
      ? 'cratehackersx:/'
      : window.location.origin;

    // Generate a session ID for polling (used by desktop apps)
    const sessionId = isDesktopApp
      ? `tidal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      : undefined;

    console.log(
      '[Tidal Auth] isDesktopApp:',
      isDesktopApp,
      'target:',
      actualTarget,
      'sessionId:',
      sessionId,
    );

    // Use auth bridge if configured, otherwise direct redirect
    let redirectUri: string;
    let stateParam: string | undefined;

    if (config.TIDAL_AUTH_BRIDGE_URL) {
      // Use the auth bridge - encode target, sessionId, and codeVerifier in state
      // Note: Including codeVerifier allows the auth bridge to complete token exchange
      // This is slightly less secure than pure PKCE but necessary for external browser auth
      redirectUri = `${config.TIDAL_AUTH_BRIDGE_URL}/api/tidal/callback`;
      const stateData: {
        target: string;
        originalState: string;
        sessionId?: string;
        cv?: string; // codeVerifier for server-side token exchange
      } = {
        target: actualTarget,
        originalState: internalRedirectUri,
      };
      if (sessionId) {
        stateData.sessionId = sessionId;
        // Only include codeVerifier for desktop apps (external browser auth)
        stateData.cv = codeVerifier;
      }
      stateParam = btoa(JSON.stringify(stateData));
      console.log('[Tidal Auth] Using auth bridge:', redirectUri);
    } else {
      // Direct redirect (fallback)
      redirectUri = isDesktopApp
        ? this.REDIRECT_URI_ELECTRON
        : this.REDIRECT_URI_WEB;
      console.log('[Tidal Auth] Direct redirect:', redirectUri);
    }

    window.localStorage.setItem('tidal_code_verifier', codeVerifier);
    window.localStorage.setItem('tidal_redirect', internalRedirectUri);
    if (sessionId) {
      window.localStorage.setItem('tidal_session_id', sessionId);
    }

    // Use login.tidal.com for authorization (NOT auth.tidal.com)
    const authorizationUrl = new URL(this.loginUrl + '/authorize');
    const params: Record<string, string> = {
      response_type: 'code',
      client_id: this.CLIENT_ID,
      scope: this.SCOPES.join(' '),
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      redirect_uri: redirectUri,
    };

    if (stateParam) {
      params.state = stateParam;
    }

    authorizationUrl.search = new URLSearchParams(params).toString();
    return {
      authUrl: authorizationUrl.toString(),
      redirectUri,
      codeVerifier,
    };
  }

  /**
   * Poll the auth bridge for tokens (used by desktop apps)
   * The auth bridge exchanges code for tokens server-side
   */
  public static async pollForTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  } | null> {
    const sessionId = localStorage.getItem('tidal_session_id');
    if (!sessionId || !config.TIDAL_AUTH_BRIDGE_URL) {
      return null;
    }

    try {
      const response = await fetch(
        `${config.TIDAL_AUTH_BRIDGE_URL}/api/tidal/tokens?sessionId=${encodeURIComponent(sessionId)}`,
      );

      if (response.status === 404) {
        // Not ready yet
        return null;
      }

      if (!response.ok) {
        console.error('[Tidal] Poll error:', response.status);
        return null;
      }

      const data = await response.json();
      // Clear the session ID after successful retrieval
      localStorage.removeItem('tidal_session_id');
      return data;
    } catch (error) {
      console.error('[Tidal] Poll error:', error);
      return null;
    }
  }

  /**
   * Start polling for tokens and complete the auth flow
   * The auth bridge handles token exchange server-side
   */
  public static async waitForAuthCode(
    onComplete: (success: boolean) => void,
    maxAttempts = 60,
  ): Promise<void> {
    let attempts = 0;
    const pollInterval = setInterval(async () => {
      attempts++;
      console.log(`[Tidal] Polling for tokens (attempt ${attempts})...`);

      const tokens = await this.pollForTokens();

      if (tokens) {
        clearInterval(pollInterval);
        console.log('[Tidal] Got tokens from bridge!');

        const expiry = Date.now() + tokens.expiresIn * 1000;
        getAppStore().dispatch(
          setToken(
            AppIdentifiers.Tidal,
            tokens.accessToken,
            tokens.refreshToken,
            expiry,
          ),
        );

        // Store the tokens directly (already exchanged by bridge)
        localStorage.setItem('tidal_access_token', tokens.accessToken);
        localStorage.setItem('tidal_refresh_token', tokens.refreshToken);
        localStorage.setItem('tidal_token_expiry', String(expiry));

        console.log('[Tidal] Tokens stored successfully!');
        onComplete(true);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        console.log('[Tidal] Polling timed out');
        localStorage.removeItem('tidal_session_id');
        onComplete(false);
      }
    }, 2000); // Poll every 2 seconds
  }

  /**
   * Logout from Tidal - clears all stored tokens and credentials
   */
  public static logout(): void {
    console.log('[Tidal] Logging out - clearing all credentials');
    getAppStore().dispatch(setToken(AppIdentifiers.Tidal, null, null, null));
    localStorage.removeItem('tidal_access_token');
    localStorage.removeItem('tidal_refresh_token');
    localStorage.removeItem('tidal_code_verifier');
    localStorage.removeItem('tidal_redirect');
    localStorage.removeItem('tidal_session_id');
    localStorage.removeItem('tidal_token_expiry');
  }

  public static async getToken(code: string, passedCodeVerifier?: string) {
    // Use passed codeVerifier (from Electron callback) or fall back to localStorage
    const codeVerifier =
      passedCodeVerifier || localStorage.getItem('tidal_code_verifier') || '';

    // The redirect_uri must match what was used in the authorization request
    // If using auth bridge, use the bridge URL; otherwise use direct URL
    let redirectUri: string;
    if (config.TIDAL_AUTH_BRIDGE_URL) {
      redirectUri = `${config.TIDAL_AUTH_BRIDGE_URL}/api/tidal/callback`;
    } else {
      // isElectron() is authoritative - don't check localhost
      redirectUri = isElectron()
        ? this.REDIRECT_URI_ELECTRON
        : this.REDIRECT_URI_WEB;
    }
    console.log('[Tidal Token] Using redirect URI:', redirectUri);
    console.log('[Tidal Token] Code verifier present:', !!codeVerifier);

    // Use Pipelines API to exchange code for tokens (keeps client secret secure)
    const apiUrl = config.PIPELINES_API_URL || config.API_BASE_URL;
    console.log('[Tidal Token] API URL:', `${apiUrl}/api/tidal/token`);

    const response = await fetch(`${apiUrl}/api/tidal/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: code,
        redirectUri: redirectUri,
        codeVerifier: codeVerifier,
      }),
    });

    console.log('[Tidal Token] Response status:', response.status);
    const result = await response.json();
    console.log('[Tidal Token] Response:', result);

    localStorage.removeItem('tidal_code_verifier');
    return result;
  }

  public static async refreshToken(forced?: boolean): Promise<boolean> {
    const tokenExpiration = this.getTokenExpiration() ?? 0;
    const refreshToken = this.getRefreshToken();

    if ((!forced && tokenExpiration > Date.now()) || !refreshToken) {
      return !forced || tokenExpiration > Date.now();
    }

    // Use Pipelines API to refresh token (keeps client secret secure)
    const apiUrl = config.PIPELINES_API_URL || config.API_BASE_URL;
    const response = await fetch(`${apiUrl}/api/tidal/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: refreshToken,
      }),
    });

    const responseJson = await response.json();

    // Response uses camelCase from our Pipelines API
    if (response.ok && responseJson.accessToken) {
      const expiry = Date.now() + Number(responseJson.expiresIn) * 1000;
      getAppStore().dispatch(
        setToken(
          AppIdentifiers.Tidal,
          responseJson.accessToken,
          responseJson.refreshToken || refreshToken,
          expiry,
        ),
      );
      localStorage.setItem('tidal_access_token', responseJson.accessToken);
      localStorage.setItem(
        'tidal_refresh_token',
        responseJson.refreshToken || refreshToken,
      );
      localStorage.setItem('tidal_token_expiry', String(expiry));
      return true;
    }

    console.error('[Tidal] Token refresh failed:', responseJson);
    this.logout();
    return false;
  }

  public static async getProfile() {
    await this.refreshToken();
    const response = await this.callApi('/users/me');
    return response;
  }

  public static async getPlaylists() {
    await this.refreshToken();
    const profile = await this.getProfile();
    // JSON:API returns data in a different structure
    const userId = profile?.data?.id;

    if (!userId) {
      console.error('[Tidal] Profile response:', profile);
      throw new Error('Unable to get user ID from profile');
    }

    // Tidal API uses userCollections for user's playlists
    const response = await this.callApi(
      `/userCollections/${userId}/relationships/playlists?include=playlists`,
    );
    return response;
  }

  public static async getPlaylistTracks(playlistId: string) {
    await this.refreshToken();
    // JSON:API format: use relationships endpoint with include
    const response = await this.callApi(
      `/playlists/${playlistId}/relationships/items?include=items`,
    );
    return response;
  }

  /**
   * Get playlist details including track count
   */
  public static async getPlaylist(
    playlistId: string,
  ): Promise<{ id: string; name?: string; numberOfTracks: number } | null> {
    try {
      await this.refreshToken();
      // Fetch playlist items to count tracks
      const response = await this.callApi(
        `/playlists/${playlistId}/relationships/items`,
      );
      const trackCount = response?.data?.length || 0;
      return {
        id: playlistId,
        numberOfTracks: trackCount,
      };
    } catch (error) {
      console.error('[Tidal] Error fetching playlist:', error);
      return null;
    }
  }

  /**
   * Get playlist tracks as iTrack[] for comparison
   */
  public static async getPlaylistTracksAsITrack(
    playlistId: string,
  ): Promise<iTrack[]> {
    try {
      await this.refreshToken();
      const response = await this.callApi(
        `/playlists/${playlistId}/relationships/items?include=items`,
      );

      if (!response?.included) {
        return [];
      }

      // Build artist map from included data
      const artistMap = new Map<string, string>();
      for (const item of response.included || []) {
        if (item.type === 'artists') {
          artistMap.set(item.id, item.attributes?.name || 'Unknown Artist');
        }
      }

      // Map track items to iTrack format
      const tracks: iTrack[] = [];
      for (const item of response.included || []) {
        if (item.type === 'tracks') {
          const mapped = this.mapTrackItem(item, artistMap);
          if (mapped) {
            tracks.push(mapped);
          }
        }
      }

      return tracks;
    } catch (error) {
      console.error('[Tidal] Error fetching playlist tracks:', error);
      return [];
    }
  }

  // Cache country code to avoid repeated API calls
  private static cachedCountryCode: string | null = null;

  private static async getCountryCode(): Promise<string> {
    if (this.cachedCountryCode) {
      return this.cachedCountryCode;
    }

    try {
      const profile = await this.getProfile();
      if (profile?.data?.attributes?.country) {
        this.cachedCountryCode = profile.data.attributes.country;
        return this.cachedCountryCode as string;
      }
    } catch (e) {}

    return 'US';
  }

  /**
   * Parse ISO 8601 duration format (PT3M59S) to milliseconds
   */
  private static parseIsoDuration(duration: string): number | undefined {
    if (!duration) return undefined;

    // Match pattern like PT3M59S, PT4M, PT30S, PT1H2M3S
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return undefined;

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);

    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  /**
   * Search for a track by ISRC code - more reliable for cross-platform matching
   */
  public static async searchByIsrc(isrc: string): Promise<iTrack[]> {
    await this.refreshToken();

    const countryCode = await this.getCountryCode();
    // Use the tracks endpoint with ISRC filter
    const endpoint = `/tracks?filter[isrc]=${isrc}&countryCode=${countryCode}&include=artists`;

    try {
      return this.mapTracksResponse(await this.callApi(endpoint));
    } catch (error) {
      return [];
    }
  }

  /** Artist names by id from a JSON:API `included` array. */
  private static artistNames(included: any[] = []): Map<string, string> {
    const names = new Map<string, string>();
    included.forEach((item: any) => {
      if (item.type === 'artists' && item.id && item.attributes?.name) {
        names.set(item.id, item.attributes.name);
      }
    });
    return names;
  }

  /** Map a `/tracks` response requested with `include=artists`. */
  private static mapTracksResponse(response: any): iTrack[] {
    const artistMap = this.artistNames(response?.included);
    return (response?.data || []).map((item: any) =>
      this.mapTrackItem(item, artistMap),
    );
  }

  /**
   * Search results don't say who each track is by, so look the tracks up
   * again with their artists. Empty when that lookup fails.
   */
  private static async getTracksWithArtists(
    ids: string[],
    countryCode: string,
  ): Promise<iTrack[]> {
    const filter = ids
      .map((id) => `filter[id]=${encodeURIComponent(id)}`)
      .join('&');

    try {
      const tracks = this.mapTracksResponse(
        await this.callApi(
          `/tracks?${filter}&countryCode=${countryCode}&include=artists`,
        ),
      );
      // Keep the search's relevance order.
      return ids
        .map((id) => tracks.find((track) => track.ID === id))
        .filter((track): track is iTrack => !!track);
    } catch (error) {
      console.warn('[Tidal] Could not load artists for search results:', error);
      return [];
    }
  }

  /**
   * Map a Tidal track item to iTrack format
   */
  private static mapTrackItem(
    item: any,
    artistMap: Map<string, string>,
  ): iTrack {
    // Try multiple ways to extract artist name
    let artist = '';

    // Method 1: Resolve artist relationships from JSON:API included data
    const artistRefs = item.relationships?.artists?.data;
    if (artistRefs?.length > 0) {
      artist = artistRefs
        .map((ref: any) => artistMap.get(ref.id) || '')
        .filter((name: string) => name)
        .join(', ');
    }

    // Method 2: artists array directly in attributes (older format)
    if (!artist && item.attributes?.artists?.length > 0) {
      artist = item.attributes.artists.map((a: any) => a.name).join(', ');
    }

    // Method 3: artistName field (some responses)
    if (!artist && item.attributes?.artistName) {
      artist = item.attributes.artistName;
    }

    // Method 4: artist object in attributes
    if (!artist && item.attributes?.artist?.name) {
      artist = item.attributes.artist.name;
    }

    // Method 5: Extract from title if still no artist (feat. patterns)
    // This is a fallback when API doesn't provide artist data
    let title = item.attributes?.title || '';
    const version = item.attributes?.version;

    // IMPORTANT: Tidal stores remix/edit info in a separate "version" field
    // Combine title + version for proper matching (e.g., "World on Fire" + "VAVO Remix")
    if (version) {
      title = `${title} (${version})`;
    }

    if (!artist) {
      // Try to extract from search context or leave as "Unknown Artist"
      // For matching purposes, we'll rely more on title/ISRC
      artist = 'Unknown Artist';
    }

    return {
      ID: item.id,
      Artist: artist,
      Title: title,
      Album: item.attributes?.album?.title || undefined,
      AlbumCover: item.attributes?.album?.imageCover
        ? `https://resources.tidal.com/images/${item.attributes.album.imageCover[0]?.url?.replace(/-/g, '/')}/320x320.jpg`
        : '',
      duration: this.parseIsoDuration(item.attributes?.duration),
      isrc: item.attributes?.isrc || undefined,
      BPM: undefined,
      Key_Camelot: undefined,
      PreviewURL: undefined,
    };
  }

  public static async searchTracks(query: string): Promise<iTrack[]> {
    await this.refreshToken();

    const countryCode = await this.getCountryCode();

    // Use the main searchResults endpoint with include=tracks,artists
    const searchId = encodeURIComponent(query);
    const endpoint = `/searchResults/${searchId}?include=tracks,artists&countryCode=${countryCode}`;

    try {
      const response = await this.callApi(endpoint);

      // JSON:API format: tracks are in the 'included' array
      const included = response?.included || [];

      const filteredTracks = included
        .filter((item: any) => item.type === 'tracks')
        .slice(0, 10);

      console.log(`[Tidal] Found ${filteredTracks.length} tracks`);

      if (!filteredTracks.length) {
        return [];
      }

      const tracksWithArtists = await this.getTracksWithArtists(
        filteredTracks.map((item: any) => item.id),
        countryCode,
      );
      if (tracksWithArtists.length) {
        return tracksWithArtists;
      }

      const artistMap = this.artistNames(included);
      return filteredTracks.map((item: any) =>
        this.mapTrackItem(item, artistMap),
      );
    } catch (error) {
      console.error('[Tidal] Search error:', error);
      return [];
    }
  }

  public static async createPlaylist(
    name: string,
    description?: string,
  ): Promise<any> {
    await this.refreshToken();

    // Tidal API uses JSON:API format
    const response = await this.callApi('/playlists', 'POST', {
      data: {
        type: 'playlists',
        attributes: {
          name: name,
          description: description || 'Created by Crate Hackers',
          privacy: 'PRIVATE',
        },
      },
    });

    if (!response?.data?.id) {
      console.error('[Tidal] No playlist ID in response:', response);
    }

    return response;
  }

  public static async addTracksToPlaylist(
    playlistId: string,
    trackIds: string[],
  ): Promise<boolean> {
    await this.refreshToken();

    console.log(
      `[Tidal] Adding ${trackIds.length} tracks to playlist ${playlistId}`,
    );

    // Tidal API limit: max 20 tracks per request
    const BATCH_SIZE = 20;
    const batchCount = Math.ceil(trackIds.length / BATCH_SIZE);
    let allAdded = true;

    for (let i = 0; i < trackIds.length; i += BATCH_SIZE) {
      const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
      const batch = trackIds.slice(i, i + BATCH_SIZE);
      const items = batch.map((id) => ({
        id: id,
        type: 'tracks',
      }));

      console.log(
        `[Tidal] Adding batch ${batchNumber}/${batchCount} (${batch.length} tracks)`,
      );

      try {
        await this.callApi(
          `/playlists/${playlistId}/relationships/items`,
          'POST',
          {
            data: items,
          },
        );
      } catch (error) {
        allAdded = false;
        console.error(
          `[Tidal] Error adding batch ${batchNumber}/${batchCount}:`,
          error,
        );
      }
    }

    return allAdded;
  }

  /**
   * Clear all tracks from a playlist (for sync updates)
   */
  public static async clearPlaylist(playlistId: string): Promise<boolean> {
    await this.refreshToken();

    console.log(`[Tidal] Clearing playlist ${playlistId}`);

    try {
      // First, get all track IDs in the playlist
      const response = await this.callApi(
        `/playlists/${playlistId}/relationships/items`,
      );

      const trackIds = response?.data?.map((item: any) => item.id) || [];

      if (trackIds.length === 0) {
        console.log('[Tidal] Playlist is already empty');
        return true;
      }

      console.log(`[Tidal] Removing ${trackIds.length} tracks from playlist`);

      // Remove tracks in batches
      const BATCH_SIZE = 20;
      for (let i = 0; i < trackIds.length; i += BATCH_SIZE) {
        const batch = trackIds.slice(i, i + BATCH_SIZE);
        const items = batch.map((id: string) => ({
          id: id,
          type: 'tracks',
        }));

        await this.callApi(
          `/playlists/${playlistId}/relationships/items`,
          'DELETE',
          {
            data: items,
          },
        );
      }

      return true;
    } catch (error) {
      console.error('[Tidal] Error clearing playlist:', error);
      return false;
    }
  }

  /**
   * Delete a playlist from Tidal
   */
  public static async deletePlaylist(playlistId: string): Promise<boolean> {
    await this.refreshToken();

    console.log(`[Tidal] Deleting playlist ${playlistId}`);

    try {
      const response = await fetch(`${this.apiUrl}/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.getAccessToken()}`,
          'Content-Type': 'application/vnd.api+json',
        },
      });

      if (response.ok || response.status === 204) {
        console.log('[Tidal] Playlist deleted successfully');
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Tidal] Failed to delete playlist:', {
          status: response.status,
          error: errorData,
        });
        return false;
      }
    } catch (error) {
      console.error('[Tidal] Error deleting playlist:', error);
      return false;
    }
  }

  public static async exportPlaylist(
    playlistName: string,
    tracks: iTrack[],
    onProgress?: (current: number, total: number, status: string) => void,
  ): Promise<{
    success: boolean;
    successTracks: iTrack[];
    failedTracks: iTrack[];
  }> {
    const result = {
      success: false,
      successTracks: [] as iTrack[],
      failedTracks: [] as iTrack[],
    };

    try {
      onProgress?.(0, tracks.length, 'Creating playlist...');

      const playlist = await this.createPlaylist(playlistName);
      const playlistId = playlist?.data?.id;

      if (!playlistId) {
        throw new Error('Failed to create playlist');
      }

      const trackIds: string[] = [];

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        onProgress?.(
          i,
          tracks.length,
          `Searching: ${track.Artist} - ${track.Title}`,
        );

        try {
          const searchResults = await this.searchTracks(
            `${track.Artist} ${track.Title}`,
          );

          if (searchResults.length > 0) {
            trackIds.push(searchResults[0].ID);
            result.successTracks.push(track);
          } else {
            result.failedTracks.push(track);
          }
        } catch {
          result.failedTracks.push(track);
        }
      }

      if (trackIds.length > 0) {
        onProgress?.(
          tracks.length,
          tracks.length,
          'Adding tracks to playlist...',
        );
        await this.addTracksToPlaylist(playlistId, trackIds);
      }

      result.success = result.successTracks.length > 0;
      onProgress?.(tracks.length, tracks.length, 'Complete!');
    } catch (error) {
      console.error('Error exporting to Tidal:', error);
      result.failedTracks = [...tracks];
    }

    return result;
  }

  private static readonly SESSION_EXPIRED_MESSAGE =
    'Tidal session expired. Please reconnect to Tidal.';

  private static async callApi(
    endpoint: string,
    method = 'GET',
    body?: any,
    retryCount = 0,
    authRetryCount = 0,
  ): Promise<any> {
    const maxRetries = 3;
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      throw new Error('No Tidal access token available');
    }

    // Tidal API uses JSON:API format which requires specific headers
    // Accept header is REQUIRED for all requests
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/vnd.api+json',
    };

    // JSON:API requires application/vnd.api+json Content-Type for POST/PATCH/DELETE
    if (method === 'POST' || method === 'PATCH' || method === 'DELETE') {
      headers['Content-Type'] = 'application/vnd.api+json';
    }

    const options: RequestInit = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const url = this.apiUrl + endpoint;
    console.log(`[Tidal API] ${method} ${url}`);

    // Rate limiting: wait before each request
    await this.rateLimitDelay();

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[Tidal API] Error ${response.status}:`, errorText);

      if (response.status === 401) {
        if (authRetryCount >= 1) {
          this.logout();
          throw new Error(this.SESSION_EXPIRED_MESSAGE);
        }

        const previousToken = accessToken;
        const refreshed = await this.refreshToken(true);
        const newToken = this.getAccessToken();

        if (!refreshed || !newToken || newToken === previousToken) {
          this.logout();
          throw new Error(this.SESSION_EXPIRED_MESSAGE);
        }

        return this.callApi(
          endpoint,
          method,
          body,
          retryCount,
          authRetryCount + 1,
        );
      }

      // Handle rate limiting with exponential backoff
      if (response.status === 429 && retryCount < maxRetries) {
        const retryAfter = response.headers.get('Retry-After');
        const baseWait = retryAfter ? parseInt(retryAfter) * 1000 : 2000;
        const waitTime = baseWait * Math.pow(2, retryCount); // Exponential backoff
        console.log(
          `[Tidal API] Rate limited (attempt ${retryCount + 1}/${maxRetries}), waiting ${waitTime}ms...`,
        );
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return this.callApi(
          endpoint,
          method,
          body,
          retryCount + 1,
          authRetryCount,
        );
      }

      throw new Error(`Tidal API error: ${response.status} - ${errorText}`);
    }

    // Always read as text first to handle empty responses safely
    const text = await response.text();

    // Handle empty responses (common for POST/DELETE operations)
    if (!text || text.trim() === '') {
      console.log(`[Tidal API] Response: (empty - success)`);
      return { success: true };
    }

    // Try to parse as JSON
    try {
      const data = JSON.parse(text);
      console.log(`[Tidal API] Response:`, data);
      return data;
    } catch {
      // If not valid JSON, return success with raw text
      console.log(`[Tidal API] Response (non-JSON):`, text.substring(0, 100));
      return { success: true, raw: text };
    }
  }

  // Rate limiting: ensure minimum delay between API calls
  // Tidal has very strict rate limits - need ~1 second between calls
  private static lastCallTime = 0;
  private static async rateLimitDelay(): Promise<void> {
    const minDelay = 1000; // 1 second between calls - Tidal is VERY strict
    const now = Date.now();
    const timeSinceLastCall = now - this.lastCallTime;
    if (timeSinceLastCall < minDelay) {
      const waitTime = minDelay - timeSinceLastCall;
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
    this.lastCallTime = Date.now();
  }

  public static isConnected(): boolean {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!token || !!refreshToken;
  }
}
